// Всплывающая панель у кнопки: выбор модели, меню. Открывается туда, где больше места,
// закрывается по Esc и клику мимо, возвращает фокус на кнопку.
import clsx from 'clsx';
import { type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const GAP = 6;
const EDGE = 12;

export function Popover({
  anchor,
  open,
  onClose,
  label,
  prefer = 'bottom',
  align = 'start',
  width = 340,
  className,
  children,
}: {
  anchor: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  label: string;
  prefer?: 'top' | 'bottom';
  align?: 'start' | 'end';
  width?: number;
  className?: string;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number; maxHeight: number } | null>(
    null,
  );

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const place = () => {
      const a = anchor.current?.getBoundingClientRect();
      if (!a) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const w = Math.min(width, vw - EDGE * 2);
      const below = vh - a.bottom - GAP - EDGE;
      const above = a.top - GAP - EDGE;
      // Сторона, где панель поместится целиком (или хотя бы на 320 px); иначе — где больше места
      const want = Math.min(box.current?.scrollHeight ?? 0, 320);
      const down = prefer === 'bottom' ? below >= want || below >= above : !(above >= want || above >= below);
      let left = align === 'start' ? a.left : a.right - w;
      left = Math.max(EDGE, Math.min(left, vw - w - EDGE));
      setPos(
        down
          ? { left, top: a.bottom + GAP, maxHeight: below }
          : { left, bottom: vh - a.top + GAP, maxHeight: above },
      );
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open, anchor, prefer, align, width]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (box.current?.contains(t) || anchor.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
      anchor.current?.focus();
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, onClose, anchor]);

  if (!open) return null;
  return createPortal(
    <div
      ref={box}
      role="dialog"
      aria-label={label}
      className={clsx('popover', className)}
      style={{
        width: Math.min(width, window.innerWidth - EDGE * 2),
        left: pos?.left ?? -9999,
        top: pos?.top,
        bottom: pos?.bottom,
        maxHeight: pos?.maxHeight,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
